/**
 * useIndustryCourses Hook
 * CRUD operations for industry_courses table (Education)
 * Automatically filters by company_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryCourse } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';
import type { PostgrestError } from '@supabase/supabase-js';

interface UseIndustryCoursesOptions {
  domain?: CompanyType;
  status?: IndustryCourse['status'] | IndustryCourse['status'][];
}

type CourseStatus = 'draft' | 'published' | 'in_progress' | 'completed' | 'cancelled';

interface CreateCourseInput {
  name: string;
  description?: string;
  course_type?: string;
  price: number;
  installments_allowed?: boolean;
  max_installments?: number;
  max_students?: number;
  current_students?: number;
  duration_hours?: number;
  start_date?: string;
  end_date?: string;
  status?: CourseStatus;
  is_active?: boolean;
  metadata?: Record<string, unknown>;
}

interface UpdateCourseInput extends Partial<CreateCourseInput> {
  id: string;
}

export function useIndustryCourses(options: UseIndustryCoursesOptions = {}) {
  const { status, domain } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sanitizePayload = <T extends Record<string, unknown>>(payload: T) =>
    Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined)
    ) as T;

  const applyDomainMetadata = (metadata?: Record<string, unknown>) => {
    const nextMetadata = { ...(metadata ?? {}) };
    if (domain && nextMetadata.domain === undefined) {
      nextMetadata.domain = domain;
    }
    return nextMetadata;
  };

  const isRelationMissingError = (error: PostgrestError | null | undefined) =>
    !!error && error.code === 'PGRST301';

  const missingRelationMessage =
    'Tabela industry_courses não encontrada. Execute as migrações recentes no Supabase e tente novamente.';

  // Get current company
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch courses
  const {
    data: courses,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry_courses', company?.id, status],
    queryFn: async () => {
      if (!company) return [];

      let query = supabase
        .from('industry_courses')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (domain) {
        query = query.contains('metadata', { domain });
      }

      if (status) {
        if (Array.isArray(status)) {
          query = query.in('status', status);
        } else {
          query = query.eq('status', status);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isRelationMissingError(error)) {
          console.warn(missingRelationMessage);
          return [];
        }
        throw error;
      }
      return data as IndustryCourse[];
    },
    enabled: !!company,
  });

  // Create course
  const createCourse = useMutation({
    mutationFn: async (input: CreateCourseInput) => {
      if (!company) throw new Error('No company found');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { metadata: inputMetadata, ...rest } = input;
      const payload = sanitizePayload({
        ...rest,
        metadata: applyDomainMetadata(inputMetadata),
        company_id: company.id,
        user_id: user.id,
        current_students: input.current_students ?? 0,
      });

      const { data, error } = await supabase
        .from('industry_courses')
        .insert(payload)
        .select()
        .single();

      if (error) {
        if (isRelationMissingError(error)) {
          throw new Error(missingRelationMessage);
        }
        throw error;
      }
      return data as IndustryCourse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_courses', company?.id],
      });
      toast({
        title: 'Curso criado',
        description: `${data.name} foi criado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar curso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update course
  const updateCourse = useMutation({
    mutationFn: async (input: UpdateCourseInput) => {
      const { id, metadata: inputMetadata, ...rest } = input;
      const payload = sanitizePayload({
        ...rest,
        metadata: inputMetadata ? applyDomainMetadata(inputMetadata) : undefined,
      });

      const { data, error } = await supabase
        .from('industry_courses')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isRelationMissingError(error)) {
          throw new Error(missingRelationMessage);
        }
        throw error;
      }
      return data as IndustryCourse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_courses', company?.id],
      });
      toast({
        title: 'Curso atualizado',
        description: `${data.name} foi atualizado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar curso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete course
  const deleteCourse = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('industry_courses')
        .delete()
        .eq('id', id);

      if (error) {
        if (isRelationMissingError(error)) {
          throw new Error(missingRelationMessage);
        }
        throw error;
      }
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industry_courses', company?.id],
      });
      toast({
        title: 'Curso excluído',
        description: 'O curso foi excluído com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir curso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    courses: courses || [],
    isLoading,
    error,
    refetch,
    createCourse: createCourse.mutate,
    createCourseAsync: createCourse.mutateAsync,
    updateCourse: updateCourse.mutate,
    updateCourseAsync: updateCourse.mutateAsync,
    deleteCourse: deleteCourse.mutate,
    deleteCourseAsync: deleteCourse.mutateAsync,
    isCreating: createCourse.isPending,
    isUpdating: updateCourse.isPending,
    isDeleting: deleteCourse.isPending,
  };
}
